import type { WatchQueryFetchPolicy } from '@apollo/client'
import { ShiftManagementStatsDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useLazyQuery, useQuery } from 'modules/core/hooks/useQuery'

import { type StatsContent } from 'modules/shift_management/hooks/reducers/schedule'
import { useFilters } from 'modules/shift_management/hooks/useFilters'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import ShiftsConflicts from 'modules/shift_management/store/internal/collections/ShiftsConflicts'

interface Props {
  fetchPolicy?: WatchQueryFetchPolicy
}
const useFetchStats = ({ fetchPolicy = 'cache-and-network' }: Props = {}) => {
  const { company } = useCurrentSession()
  const [, dispatchSchedule] = useSchedule()
  const [filters] = useFilters()
  const [{ view }] = useSchedule()
  const { startAt, endAt } = view

  const { loading, data, refetch } = useQuery(ShiftManagementStatsDocument, {
    variables: {
      companyId: company.id,
      customFilters: filters.customFilters,
      // - Accept employeeIds to refresh some isolated rows on update/delete
      employeeIds: filters.employeeIds || null,
      locationIds: filters.locationIds,
      endAt,
      logicOperator: filters.logicOperator,
      startAt,
      terminated: filters.terminated,
      teamIds: filters.teamIds,
      legalEntityIds: filters.legalEntityIds,
      withoutShiftIds: filters.withoutShiftIds || null,
    },
    fetchPolicy,
  })
  const stats: StatsContent = useUnsafeMemo(() => {
    const result = data?.shiftManagement?.statsConnection?.nodes[0]
    return {
      hasDrafts: result?.hasDrafts ?? false,
      hasDraftsWithoutConflict: result?.hasDraftsWithoutConflict ?? false,
      shiftConflicts: ShiftsConflicts.buildFromGraphQL(result?.shiftConflicts || []),
    }
  }, [data?.shiftManagement.statsConnection])

  const refetchData = async () => {
    const { data: statsData } = await refetch()
    const result = statsData?.shiftManagement?.statsConnection?.nodes[0]
    dispatchSchedule({
      type: 'BOOTSTRAP',
      stats: {
        hasDrafts: result?.hasDrafts ?? false,
        hasDraftsWithoutConflict: result?.hasDraftsWithoutConflict ?? false,
        shiftConflicts: ShiftsConflicts.buildFromGraphQL(result?.shiftConflicts || []),
      },
      leaves: undefined,
      holidays: undefined,
      restDays: undefined,
      shifts: undefined,
    })
  }

  useUnsafeEffect(() => {
    dispatchSchedule({
      type: 'SET_STATS_LOADING',
      statsLoading: loading,
    })
  }, [loading])

  useUnsafeEffect(() => {
    if (loading) return

    dispatchSchedule({
      type: 'BOOTSTRAP',
      stats: stats,
      leaves: undefined,
      holidays: undefined,
      restDays: undefined,
      shifts: undefined,
    })
  }, [dispatchSchedule, loading, stats])

  return { stats, loading, refetchData }
}

const useLazyFetchStats = () => {
  const { company } = useCurrentSession()
  const [, dispatchSchedule] = useSchedule()
  const [filters] = useFilters()
  const [{ view }] = useSchedule()
  const { startAt, endAt } = view

  const [executeFetchQuery] = useLazyQuery(ShiftManagementStatsDocument)

  const refetchData = async () => {
    const { data: statsData } = await executeFetchQuery({
      query: ShiftManagementStatsDocument,
      variables: {
        companyId: company.id,
        customFilters: filters.customFilters,
        // - Accept employeeIds to refresh some isolated rows on update/delete
        employeeIds: filters.employeeIds || null,
        locationIds: filters.locationIds,
        endAt,
        logicOperator: filters.logicOperator,
        startAt,
        terminated: filters.terminated,
        teamIds: filters.teamIds,
        legalEntityIds: filters.legalEntityIds,
      },
    })
    const result = statsData?.shiftManagement?.statsConnection?.nodes[0]
    dispatchSchedule({
      type: 'BOOTSTRAP',
      stats: {
        hasDrafts: result?.hasDrafts ?? false,
        hasDraftsWithoutConflict: result?.hasDraftsWithoutConflict ?? false,
        shiftConflicts: ShiftsConflicts.buildFromGraphQL(result?.shiftConflicts || []),
      },
      leaves: undefined,
      holidays: undefined,
      restDays: undefined,
      shifts: undefined,
    })

    return {
      stats: {
        hasDrafts: result?.hasDrafts ?? false,
        hasDraftsWithoutConflict: result?.hasDraftsWithoutConflict ?? false,
        shiftConflicts: ShiftsConflicts.buildFromGraphQL(result?.shiftConflicts || []),
      },
    }
  }

  return { refetchData }
}

export default useFetchStats
export { useLazyFetchStats }
